import { createTheme } from "@mui/material/styles";

// MUI theme that delegates to CSS custom properties for colors,
// keeping it in sync with the SCSS theme system.
const Theme = createTheme({
  palette: {
    primary: {
      main: "#009698",
    },
    secondary: {
      main: "#455c70",
    },
    error: {
      main: "#ef4444",
    },
  },
  typography: {
    fontFamily: "Nunito, sans-serif",
  },
  components: {
    MuiToolbar: {
      variants: [
        {
          props: { variant: "footer" },
          style: {
            backgroundColor: "var(--color-bg-secondary)",
            padding: "0.9em 8.2vw !important",
            color: "var(--color-text-secondary)",
            fontSize: "0.8rem",
            fontFamily: "Nunito, sans-serif",
            textAlign: "left",
            lineHeight: "1.2rem",
            marginTop: "16px",
          },
        },
      ],
    },
    MuiLink: {
      variants: [
        {
          props: { variant: "email" },
          style: {
            textTransform: "lowercase",
            "&:focus": {
              color: "var(--color-error)",
            },
          },
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--color-nav-bg)",
          color: "var(--color-nav-text)",
          height: "56px",
          minHeight: "56px",
          flexDirection: "row",
          alignItems: "center",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--color-bg-elevated)",
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "var(--color-bg-elevated)",
          border: "1px solid var(--color-border-primary)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "var(--color-text-primary)",
          "&:hover": {
            backgroundColor: "var(--color-interactive-hover)",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "var(--color-bg-elevated)",
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--color-card-bg)",
          color: "var(--color-text-primary)",
          "&::before": {
            display: "none",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontFamily: "Nunito, sans-serif",
          color: "var(--color-accent-primary)",
          "&.Mui-disabled": {
            color: "var(--color-text-muted)",
          },
        },
        containedPrimary: {
          color: "#fff",
          backgroundColor: "var(--color-accent-primary)",
          "&:hover": {
            backgroundColor: "var(--color-accent-hover)",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: "var(--color-text-secondary)",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "var(--color-text-tertiary)",
          "&.Mui-checked": {
            color: "var(--color-accent-primary)",
          },
        },
      },
    },
    // MUI's unchecked track is black at 0.38 opacity, which vanishes on the
    // dark theme; the checked track keeps the primary color.
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: "var(--color-text-tertiary)",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            color: "var(--color-text-primary)",
            backgroundColor: "var(--color-input-bg)",
            "& fieldset": {
              borderColor: "var(--color-input-border)",
            },
            "&:hover fieldset": {
              borderColor: "var(--color-border-secondary)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--color-border-focus)",
            },
          },
          "& .MuiInputLabel-root": {
            color: "var(--color-text-tertiary)",
          },
          "& .MuiInputBase-input": {
            color: "var(--color-text-primary)",
            // Placeholder sits two steps below entered text (primary) so it
            // reads as a ghosted hint, not filled-in content (accessibility).
            "&::placeholder": {
              color: "var(--color-text-tertiary)",
              opacity: 0.7,
            },
            "&:-webkit-autofill": {
              WebkitTextFillColor: "var(--color-text-primary)",
              WebkitBoxShadow: "0 0 0 100px var(--color-input-bg) inset",
            },
          },
          "& .MuiInput-root .MuiInputBase-input::placeholder": {
            color: "var(--color-text-tertiary)",
            opacity: 1,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "var(--color-text-tertiary)",
          "&:hover": {
            backgroundColor: "var(--color-interactive-hover)",
            color: "var(--color-text-primary)",
          },
        },
      },
    },
  },
});

export default Theme;
